import { useLockStore } from "../store/lockStore";
import { useLockActions } from "../hooks/useLockActions";
import { AlertCircle } from "lucide-react";

interface AccessRequestBannerProps {
  formId: string;
}

/**
 * Yellow banner shown above the form when another user is requesting access.
 * The form stays interactive underneath — the holder can keep editing while
 * deciding. Once the countdown hits 0 (or they click Give Access), the lock
 * transfers and the holder sees the RemovedScreen.
 */
export function AccessRequestBanner({ formId }: AccessRequestBannerProps) {
  const incomingRequest = useLockStore((s) => s.incomingRequest);
  const countdownSeconds = useLockStore((s) => s.countdownSeconds);
  const { grant, deny } = useLockActions(formId);

  if (!incomingRequest) return null;

  return (
    <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <AlertCircle size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 min-w-0">
            <span className="font-semibold break-words">{incomingRequest.email}</span> is requesting access to this form.
            {countdownSeconds !== null && (
              <span className="block sm:inline sm:ml-1">
                You will be removed in <span className="font-semibold tabular-nums">{countdownSeconds}s</span>.
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={deny}
            className="py-1.5 px-3 rounded-md border border-amber-300 bg-white text-amber-900 text-sm font-semibold hover:bg-amber-100 hover:border-amber-400 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300 cursor-pointer"
          >
            Keep Editing
          </button>
          <button
            type="button"
            onClick={grant}
            className="py-1.5 px-3 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300 cursor-pointer"
          >
            Give Access
          </button>
        </div>
      </div>
    </div>
  );
}
